import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Settings, Copy, Save, X, MapPin, Wand2, FileSpreadsheet, Calculator, Shield } from 'lucide-react';
import { BasicSettingsTab } from './BasicSettingsTab';
import { RiskManagementTab } from './RiskManagementTab';
import { PositionSizeMultiplierPanel } from './PositionSizeMultiplierPanel';
import { CsvConfigRow } from './CsvConfigUpload';
import { CsvPositionSizeConfigUpload } from './CsvPositionSizeConfigUpload';
import { FileParams, AnalysisParams } from './types';
import { useCustomPositionMappings } from '@/hooks/useCustomPositionMappings';
import { normalizeInstrument } from '@/utils/instrument-utils';
import { findMatchingConfig, calculatePositionSelection, validateMartingalePattern, extractFileId, getMatchingDetails } from '@/utils/csvConfigMatcher';
import { toast } from 'sonner';

interface FileParametersPanelProps {
  files: Array<{ id: string; filename: string; hasSavedParams?: boolean; data?: any[]; }>;
  perFileParams: Map<string, AnalysisParams>;
  globalDefaults: AnalysisParams;
  uniquePositionSizes: Map<string, string[]>;
  csvConfig: CsvConfigRow[];
  onUpdateFileParams: (fileId: string, params: AnalysisParams) => void;
  onUpdateGlobalDefaults: (params: AnalysisParams) => void;
  onCopyToAll: (sourceFileId: string) => void;
  onClearSavedParams?: (fileId: string) => void;
  onBulkApplyMultiplier?: (multiplier: number, fileIds: string[]) => void;
}

export const FileParametersPanel: React.FC<FileParametersPanelProps> = ({
  files,
  perFileParams,
  globalDefaults,
  uniquePositionSizes,
  csvConfig,
  onUpdateFileParams,
  onUpdateGlobalDefaults,
  onCopyToAll,
  onClearSavedParams,
  onBulkApplyMultiplier
}) => {
  const { customMappings, getMapping } = useCustomPositionMappings();
  const updateFileParam = <K extends keyof AnalysisParams>(
    fileId: string,
    key: K,
    value: AnalysisParams[K]
  ) => {
    const currentParams = perFileParams.get(fileId) || globalDefaults;
    onUpdateFileParams(fileId, { ...currentParams, [key]: value });
  };

  const updateGlobalParam = <K extends keyof AnalysisParams>(
    key: K,
    value: AnalysisParams[K]
  ) => {
    onUpdateGlobalDefaults({ ...globalDefaults, [key]: value });
  };

  // Smart symbol normalization and mapping
  const normalizeSymbol = (symbol: string): string => {
    return normalizeInstrument(symbol);
  };

  const getMappingSmart = (symbol: string) => {
    if (!symbol) return undefined;
    
    // Try original symbol first (uppercase)
    const upperSymbol = symbol.toUpperCase();
    let mapping = getMapping(upperSymbol);
    if (mapping) return { symbol: upperSymbol, mapping };
    
    // Try normalized symbol
    const normalized = normalizeSymbol(symbol);
    mapping = getMapping(normalized);
    if (mapping) return { symbol: normalized, mapping };
    
    // Try searching through all mappings with normalized comparison
    const allMappings = customMappings;
    for (const [mappedSymbol, mappingData] of allMappings) {
      if (normalizeSymbol(mappedSymbol) === normalized) {
        return { symbol: mappedSymbol, mapping: mappingData };
      }
    }
    
    return undefined;
  };

  const applyPositionMappings = (fileId: string) => {
    const file = files.find(f => f.id === fileId);
    if (!file || !file.data || file.data.length === 0) {
      toast.error('No file data available');
      return;
    }

    // Extract all position sizes from CSV data to find minimum
    const positionSizes = file.data!.map((row: any) => row.position_size).filter(size => size > 0);
    
    if (positionSizes.length === 0) {
      toast.error('No valid position sizes found in this file');
      return;
    }

    // Use minimum position size as quantity_base
    const minPositionSize = Math.min(...positionSizes);
    updateFileParam(fileId, 'quantity_base', minPositionSize);

    toast.success(`Applied minimum position size: ${minPositionSize.toFixed(4)} for ${file.filename}`);
  };

  const applyPositionMappingsToAll = (autoApply = false) => {
    const filesToProcess = files.filter(f => f.data && f.data.length > 0);
    
    if (filesToProcess.length === 0) {
      if (!autoApply) toast.error('No files with data available');
      return;
    }

    let successCount = 0;
    let skipCount = 0;
    const processedDetails: string[] = [];
    
    filesToProcess.forEach(file => {
      // Extract all position sizes from CSV data to find minimum
      const positionSizes = file.data!.map((row: any) => row.position_size).filter(size => size > 0);
      
      if (positionSizes.length === 0) {
        skipCount++;
        return;
      }

      // Use minimum position size as quantity_base
      const minPositionSize = Math.min(...positionSizes);
      updateFileParam(file.id, 'quantity_base', minPositionSize);
      successCount++;
      
      processedDetails.push(`${file.filename}: min position ${minPositionSize.toFixed(4)}`);
    });

    if (successCount > 0 && !autoApply) {
      toast.success(`Applied minimum position sizes to ${successCount} files${skipCount > 0 ? `, skipped ${skipCount} files` : ''}`, {
        description: processedDetails.slice(0, 3).join('\n') + (processedDetails.length > 3 ? `\n...and ${processedDetails.length - 3} more` : '')
      });
    } else if (successCount === 0 && !autoApply) {
      toast.error('No valid position sizes found in any files.');
    }
    
    return { successCount, skipCount };
  };

  const applyCsvPositionConfig = () => {
    if (csvConfig.length === 0) {
      toast.error('No CSV configuration loaded. Please upload a configuration file first.');
      return;
    }

    let successCount = 0;
    let skipCount = 0;
    const processedDetails: string[] = [];
    const errors: string[] = [];

    files.forEach(file => {
      const config = findMatchingConfig(file.filename, csvConfig);
      
      if (!config) {
        skipCount++;
        return;
      }

      const filePositionSizes = uniquePositionSizes.get(file.id) || [];
      if (filePositionSizes.length === 0) {
        errors.push(`${file.filename}: No position sizes available`);
        return;
      }

      // Validate Martingale pattern
      const validation = validateMartingalePattern(filePositionSizes);
      if (!validation.isValid) {
        errors.push(`${file.filename}: ${validation.message}`);
      }

      const selectedSizes = calculatePositionSelection(
        filePositionSizes,
        config.startAfter,
        config.stopAfter
      );

      if (selectedSizes.length === 0) {
        errors.push(`${file.filename}: No positions selected for range ${config.startAfter + 1}-${config.stopAfter}`);
        return;
      }

      // Update the file parameters
      const currentParams = perFileParams.get(file.id) || globalDefaults;
      onUpdateFileParams(file.id, {
        ...currentParams,
        selected_qty_values: selectedSizes
      });

      successCount++;
      processedDetails.push(`${config.id}: Selected ${selectedSizes.length} positions (Loss ${config.startAfter + 1}-${config.stopAfter})`);
    });

    // Show results
    if (successCount > 0) {
      toast.success(`Applied CSV configuration to ${successCount} files${skipCount > 0 ? `, skipped ${skipCount} files without matching config` : ''}`, {
        description: processedDetails.slice(0, 3).join('\n') + (processedDetails.length > 3 ? `\n...and ${processedDetails.length - 3} more` : '')
      });
    }

    if (errors.length > 0) {
      toast.error(`Configuration errors (${errors.length}):`, {
        description: errors.slice(0, 3).join('\n') + (errors.length > 3 ? `\n...and ${errors.length - 3} more` : '')
      });
    }

    if (successCount === 0 && skipCount > 0) {
      toast.error('No files matched the CSV configuration. Check that your ID patterns match your filenames.');
    }
  };

  const renderParameterSection = (
    fileId: string | null,
    params: AnalysisParams,
    title: string,
    isGlobal = false
  ) => {
    const filePositionSizes = fileId ? uniquePositionSizes.get(fileId) || [] : [];
    const csvData = fileId ? files.find(f => f.id === fileId)?.data || [] : [];
    
    const updateParam = (key: keyof AnalysisParams, value: any) => {
      if (isGlobal) {
        updateGlobalParam(key, value);
      } else if (fileId) {
        updateFileParam(fileId, key, value);
      }
    };

    return (
      <Tabs defaultValue="basic" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="basic" className="flex items-center gap-2">
            <Calculator className="h-4 w-4" />
            Basic Settings
          </TabsTrigger>
          <TabsTrigger value="risk" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Risk Management
          </TabsTrigger>
          <TabsTrigger value="multiplier" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Position Multiplier
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="basic" className="mt-6">
          <BasicSettingsTab
            fileId={fileId}
            params={params}
            filePositionSizes={filePositionSizes}
            isGlobal={isGlobal}
            onUpdateParam={updateParam}
            onApplyMinPositionSize={!isGlobal && fileId ? () => applyPositionMappings(fileId) : undefined}
          />
        </TabsContent>
        
        <TabsContent value="risk" className="mt-6">
          <RiskManagementTab
            fileId={fileId}
            params={params}
            csvData={csvData}
            isGlobal={isGlobal}
            onUpdateParam={updateParam}
          />
        </TabsContent>
        
        <TabsContent value="multiplier" className="mt-6">
          <PositionSizeMultiplierPanel
            fileId={fileId}
            params={params}
            files={files}
            perFileParams={perFileParams}
            isGlobal={isGlobal}
            onUpdateParam={updateParam}
            onBulkApplyMultiplier={onBulkApplyMultiplier}
          />
        </TabsContent>
      </Tabs>
    );
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Analysis Parameters
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Apply to All Files Buttons */}
          {files.length > 1 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 p-4 bg-muted/50 rounded-lg border">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => applyPositionMappingsToAll(false)}
                    className="text-sm"
                  >
                    <Wand2 className="h-4 w-4 mr-2" />
                    Apply Min Position Size to All Files
                  </Button>
                <span className="text-sm text-muted-foreground">
                  Auto-fill Quantity Base using minimum position size from each file's data
                </span>
              </div>
              
              {/* CSV Adjust Position Size - New Component */}
              <CsvPositionSizeConfigUpload 
                files={files}
                onApplyMultipliers={(multipliers) => {
                  const fileIds = Array.from(multipliers.keys());
                  fileIds.forEach(fileId => {
                    const multiplier = multipliers.get(fileId);
                    if (multiplier && onBulkApplyMultiplier) {
                      const currentParams = perFileParams.get(fileId) || globalDefaults;
                      onUpdateFileParams(fileId, {
                        ...currentParams,
                        position_size_multiplier: multiplier
                      });
                    }
                  });
                }}
              />
              
              {csvConfig.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 p-4 bg-primary/10 rounded-lg border border-primary/20">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={applyCsvPositionConfig}
                      className="text-sm border-primary/30 text-primary hover:bg-primary/10"
                    >
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                      Apply CSV Position Config
                    </Button>
                    <span className="text-sm text-primary">
                      Auto-select Position Sizes based on uploaded CSV configuration ({csvConfig.length} entries)
                    </span>
                  </div>
                  
                  {/* Enhanced Debug information with structured matching details */}
                  <div className="text-xs bg-muted/30 rounded-lg p-3 border border-border/40">
                    <div className="font-medium text-foreground mb-2 flex items-center gap-2">
                      <span>Debug: Smart File Matching System</span>
                      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                        {csvConfig.length} configs loaded
                      </span>
                    </div>
                    
                    <div className="space-y-3">
                      {files.map(file => {
                        const matchingDetails = getMatchingDetails(file.filename, csvConfig);
                        const bestMatch = matchingDetails.bestMatch;
                        const hasSymbolConfigs = matchingDetails.sameSymbolCount > 0;
                        
                        return (
                          <div key={file.id} className="border border-border/20 rounded p-2">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`w-2 h-2 rounded-full ${bestMatch ? 'bg-primary' : hasSymbolConfigs ? 'bg-warning' : 'bg-destructive'}`}></span>
                              <span className="font-mono text-xs text-foreground truncate">{file.filename}</span>
                            </div>
                            
                            <div className="ml-4 space-y-1">
                              <div className="text-xs">
                                <span className="text-muted-foreground">Sanitized:</span>
                                <span className="text-primary ml-1 font-mono">
                                  {matchingDetails.sanitizedId || 'failed to parse'}
                                </span>
                              </div>
                              
                              <div className="text-xs">
                                <span className="text-muted-foreground">Parsed:</span>
                                <span className="text-primary ml-1 font-mono">
                                  {matchingDetails.fileParsed.symbol || 'NO_SYMBOL'}
                                  {matchingDetails.fileParsed.suffix}
                                  {matchingDetails.fileParsed.tp > 0 && `-${matchingDetails.fileParsed.tp}`}
                                  {matchingDetails.fileParsed.sl > 0 && Math.abs(matchingDetails.fileParsed.sl - matchingDetails.fileParsed.tp * 2) > 0.01 && `-${matchingDetails.fileParsed.sl}`}
                                  {matchingDetails.fileParsed.timeframe && `-${matchingDetails.fileParsed.timeframe}`}
                                </span>
                              </div>
                              
                              <div className="text-xs">
                                <span className="text-muted-foreground">Symbol Configs:</span>
                                <span className={`ml-1 ${hasSymbolConfigs ? 'text-primary' : 'text-destructive'}`}>
                                  {matchingDetails.sameSymbolCount} match{matchingDetails.sameSymbolCount !== 1 ? 'es' : ''}
                                  {!hasSymbolConfigs && matchingDetails.availableSymbols.length > 0 && (
                                    <span className="text-muted-foreground">
                                      {' '}(available: {matchingDetails.availableSymbols.slice(0, 3).join(', ')}{matchingDetails.availableSymbols.length > 3 ? '...' : ''})
                                    </span>
                                  )}
                                </span>
                              </div>
                              
                              <div className="text-xs">
                                <span className="text-muted-foreground">~ Best:</span>
                                {bestMatch ? (
                                  <span className="text-primary ml-1 font-mono">
                                    {bestMatch.id} ({matchingDetails.confidence}% ✓)
                                  </span>
                                ) : hasSymbolConfigs ? (
                                  <span className="text-warning ml-1">
                                    {matchingDetails.allMatches.length > 0 
                                      ? `${matchingDetails.allMatches[0].config.id} (${matchingDetails.confidence}% - need better match)`
                                      : 'Symbol found but no good match'
                                    }
                                  </span>
                                ) : (
                                  <span className="text-destructive ml-1">
                                    {matchingDetails.allMatches.length > 0 
                                      ? `${matchingDetails.allMatches[0].config.id} (${matchingDetails.confidence}% - wrong symbol)`
                                      : 'No matching symbol in configs'
                                    }
                                  </span>
                                )}
                              </div>
                              
                              {matchingDetails.allMatches.length > 0 && (
                                <div className="text-xs mt-1 pl-2 border-l border-border/30">
                                  <span className="text-muted-foreground">Top candidates:</span>
                                  <div className="ml-1 space-y-0.5">
                                    {matchingDetails.allMatches.slice(0, 2).map((match, idx) => (
                                      <div key={idx} className="text-xs">
                                        <span className="font-mono text-primary">{match.config.id}</span>
                                        <span className="text-muted-foreground"> ({match.score}% - {match.reason})</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        <Accordion type="single" collapsible defaultValue="global">
          <AccordionItem value="global">
            <AccordionTrigger>
              <div className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Global Defaults
              </div>
            </AccordionTrigger>
            <AccordionContent>
              {renderParameterSection(null, globalDefaults, "Global Defaults", true)}
            </AccordionContent>
          </AccordionItem>

          {files.map((file) => {
            const fileParams = perFileParams.get(file.id) || globalDefaults;
            return (
              <AccordionItem key={file.id} value={file.id}>
                <AccordionTrigger>
                  <div className="flex items-center justify-between w-full mr-4">
                    <div className="flex items-center gap-2">
                      <span className="truncate">{file.filename}</span>
                      {file.hasSavedParams && (
                        <div className="flex items-center gap-1 text-xs text-primary bg-primary/10 px-2 py-1 rounded">
                          <Save className="h-3 w-3" />
                          Saved
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {file.hasSavedParams && onClearSavedParams && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            onClearSavedParams(file.id);
                          }}
                          className="text-destructive hover:text-destructive/80"
                          title="Clear saved parameters"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onCopyToAll(file.id);
                        }}
                        title="Copy to all files"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  {renderParameterSection(file.id, fileParams, file.filename)}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </CardContent>
    </Card>
  );
};